/**
 * Closed-map badge variant helpers for the webhook-failures surfaces.
 *
 * Single source of truth for the `ErrorKind` / `Provider` → badge-variant
 * mappings shared by the inbox list page, the detail drawer, and the
 * standalone detail page. Keeping the closed maps here (rather than
 * re-declaring them per surface) means a backend enum rename fails the
 * `Record<Enum, …>` exhaustiveness check in exactly one place.
 */

import { closedEnumLookup } from '@lib/closedEnumGuard';
import type { components } from '@/types/api.generated';
// The zero-dependency leaf, NOT `@/services/webhook-failures` (#1805 A3b).
// `features/admin/widgets.tsx` reaches this module, and `src/features/widgets.ts`
// is evaluated by `src/test-setup.ts` for every suite ahead of any `vi.mock`,
// so importing the service here would instantiate `@/lib/api` before a test
// could replace it. The service re-exports both maps, so this is the same
// closed set, reached without the transport.
import {
  ERROR_KIND,
  PROVIDER,
  type ErrorKind,
  type Provider,
} from '@/services/webhook-failures.constants';

export type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

/**
 * Closed `Record<ErrorKind, BadgeVariant>` — compile-time exhaustive over
 * `ERROR_KIND`. `secret_misconfigured` is a deploy-time failure (redeploy with
 * the secret, don't retry the payload) → 'outline'; `topic_not_owned` is
 * foreign-provider noise → 'secondary'; the rest are genuine handler faults →
 * 'destructive'.
 */
const ERROR_KIND_BADGE_VARIANT: Record<ErrorKind, BadgeVariant> = {
  [ERROR_KIND.SIGNATURE_VERIFY_FAILED]: 'destructive',
  [ERROR_KIND.SCHEMA_PARSE_FAILED]: 'destructive',
  [ERROR_KIND.D1_WRITE_FAILED]: 'destructive',
  [ERROR_KIND.USE_CASE_THREW]: 'destructive',
  [ERROR_KIND.SECRET_MISCONFIGURED]: 'outline',
  [ERROR_KIND.TOPIC_NOT_OWNED]: 'secondary',
};

export const badgeVariantFor = closedEnumLookup<ErrorKind, BadgeVariant>(
  ERROR_KIND_BADGE_VARIANT,
  'outline',
);

const PROVIDER_BADGE_VARIANT: Record<Provider, BadgeVariant> = {
  [PROVIDER.SHOPIFY]: 'secondary',
  [PROVIDER.ONFLEET]: 'outline',
};

export const providerBadgeVariantFor = closedEnumLookup<Provider, BadgeVariant>(
  PROVIDER_BADGE_VARIANT,
  'outline',
);

// ─── Self-heal verdict (#2017) ───────────────────────────────────────────────

/**
 * The verdict the backend attaches to every OPEN `d1_write_failed` row: did a
 * later delivery of the same webhook already land? `self_heal: null` means the
 * question does not apply (the row is resolved, or is some other error kind) —
 * it is NOT an unknown answer, and nothing renders for it.
 *
 * The closed sets come from the generated contract rather than a hand-written
 * copy: `self-heal.ts` is the only author of these unions, and a value-level
 * re-listing here is a drift source with no detector.
 */
type SelfHeal = NonNullable<components['schemas']['WebhookFailureListItem']['self_heal']>;
export type SelfHealVerdict = SelfHeal['verdict'];
export type SelfHealCriterion = NonNullable<SelfHeal['criterion']>;

/**
 * `self_healed` is good news an operator acts on (close the row), so it reads
 * as a filled badge; `needs_review` is the status quo of every row already in
 * this inbox, so it stays 'outline' rather than adding a second loud badge
 * beside an error kind that is usually 'destructive' already.
 */
const SELF_HEAL_BADGE_VARIANT: Record<SelfHealVerdict, BadgeVariant> = {
  self_healed: 'secondary',
  needs_review: 'outline',
};

export const selfHealBadgeVariantFor = closedEnumLookup<SelfHealVerdict, BadgeVariant>(
  SELF_HEAL_BADGE_VARIANT,
  'outline',
);

const SELF_HEAL_LABEL_I18N: Record<SelfHealVerdict, string> = {
  self_healed: 'webhookFailures.selfHeal.self_healed',
  needs_review: 'webhookFailures.selfHeal.needs_review',
};

/** `undefined` for a verdict this build does not know — the caller renders no
 *  badge at all rather than a raw wire token. */
export const selfHealLabelKeyFor = closedEnumLookup<SelfHealVerdict, string | undefined>(
  SELF_HEAL_LABEL_I18N,
  undefined,
);

const SELF_HEAL_CRITERION_I18N: Record<SelfHealCriterion, string> = {
  provider_event_id: 'webhookFailures.detail.selfHealCriterion.provider_event_id',
  payload_hash: 'webhookFailures.detail.selfHealCriterion.payload_hash',
};

export const selfHealCriterionKeyFor = closedEnumLookup<SelfHealCriterion, string | undefined>(
  SELF_HEAL_CRITERION_I18N,
  undefined,
);

/**
 * The sentence under the badge when no CRITERION supplies one. A self-healed
 * row is explained by the criterion that proved it (there is always one), so
 * its entry is deliberately absent; `needs_review` carries no criterion by
 * construction and needs its own line.
 */
const SELF_HEAL_EXPLANATION_I18N: Record<SelfHealVerdict, string | undefined> = {
  self_healed: undefined,
  needs_review: 'webhookFailures.detail.selfHealNeedsReview',
};

export const selfHealExplanationKeyFor = closedEnumLookup<SelfHealVerdict, string | undefined>(
  SELF_HEAL_EXPLANATION_I18N,
  undefined,
);
